/**
 * Self buff/debuff bar (#139) — WoW-style top-right tracker of the VIEWER'S OWN
 * auras, split into a buffs row and a debuffs row, each chip showing icon +
 * duration countdown + stack count.
 *
 * DISTINCT from the nameplate/frame aura rows (#137/#138): those show a curated
 * "major" subset on OTHER units; this shows (nearly) ALL of your own auras — the
 * self-status HUD. It reads the OWN entity's already-wired `auras` list
 * (`WireEntity.auras`, resolved via `ownSeat`) — NO new sim/protocol/wire data.
 *
 * PURELY COSMETIC / CLIENT-SIDE. No sim, no protocol, no hash, no SIM_VERSION.
 *
 * Behaviour:
 *   • Split buff vs debuff via the WIRE-CARRIED `polarity` (#168) — the server
 *     authors each aura's buff/debuff class on the def; the client reads it off
 *     `WireAura.polarity` (no local mirror table, no "unknown → buff" default).
 *   • Shows literally EVERY own aura at v1 (unlike the #137 major-subset filter
 *     on other units) — a `hidden`/internal-carrier exclusion is a reserved hook
 *     on `auraIcons.ts`, not built here (no internal carrier aura exists yet).
 *   • Sort each group by soonest-expiring-first; permanent (no-expiry) auras
 *     last. A total, deterministic order (see {@link compareAurasForBar}).
 *   • Anchored top-right (fixed default); all layout knobs live on
 *     {@link SelfAuraBarStyle} for later per-user customization (#48 pattern).
 *
 * data-* contract:
 *   • data-testid="self-aura-bar" — always present (empty when no auras)
 *   • data-buff-count / data-debuff-count — group sizes
 *   • child rows data-testid="self-buff-row" / "self-debuff-row"
 *   • each chip is an <AuraChip> (data-testid "self-aura-chip")
 */

import type { CSSProperties, ReactElement } from "react";
import type { WireAura } from "@pit/protocol";
import { useStore } from "../store/context.js";
import { ownEntity } from "./hud-util.js";
import { AuraChip, DEFAULT_AURA_CHIP_STYLE, type AuraChipStyle } from "./AuraChip.js";

/** Sim tick rate — matches MatchScreen / Countdown / SoundController. */
const TICK_RATE = 30;

/** Layout treatment for the self-aura bar. Every knob is data-driven. */
export interface SelfAuraBarStyle {
  /** Distance from the top viewport edge, px. */
  readonly topPx: number;
  /** Distance from the right viewport edge, px. */
  readonly rightPx: number;
  /** Gap between the buffs row and the debuffs row, px. */
  readonly rowGapPx: number;
  /** Gap between chips within a row, px. */
  readonly chipGapPx: number;
  /** Max width of a wrapping row, px (chips wrap to a grid past this). */
  readonly rowMaxWidthPx: number;
  /** Per-chip visual style. */
  readonly chip: AuraChipStyle;
}

/** Default treatment: fixed top-right, buffs above debuffs. */
export const DEFAULT_SELF_AURA_BAR_STYLE: SelfAuraBarStyle = {
  topPx: 16,
  rightPx: 16,
  rowGapPx: 8,
  chipGapPx: 4,
  rowMaxWidthPx: 336,
  chip: DEFAULT_AURA_CHIP_STYLE,
};

/**
 * Deterministic ordering for the self-bar: soonest-expiring first, permanent
 * (no-expiry) auras last. Ties broken by def id, then source seat, then applied
 * tick — a TOTAL order, so render order never wobbles between equal auras.
 */
export function compareAurasForBar(a: WireAura, b: WireAura, now: number): number {
  const aPermanent = a.expiresAtTick === null;
  const bPermanent = b.expiresAtTick === null;
  if (aPermanent !== bPermanent) return aPermanent ? 1 : -1;
  if (a.expiresAtTick !== null && b.expiresAtTick !== null) {
    const aRemaining = a.expiresAtTick - now;
    const bRemaining = b.expiresAtTick - now;
    if (aRemaining !== bRemaining) return aRemaining - bRemaining;
  }
  if (a.def !== b.def) return a.def < b.def ? -1 : 1;
  if (a.sourceSeat !== b.sourceSeat) return a.sourceSeat - b.sourceSeat;
  return a.appliedAtTick - b.appliedAtTick;
}

/**
 * Partition the viewer's auras into sorted buff / debuff groups. Pure — the
 * reducer of the whole bar, exposed so the component test drives split + sort
 * directly.
 */
export function selfBarAuras(
  auras: readonly WireAura[],
  now: number,
): { readonly buffs: readonly WireAura[]; readonly debuffs: readonly WireAura[] } {
  const buffs: WireAura[] = [];
  const debuffs: WireAura[] = [];
  for (const aura of auras) {
    if (aura.polarity === "buff") buffs.push(aura);
    else debuffs.push(aura);
  }
  const cmp = (x: WireAura, y: WireAura): number => compareAurasForBar(x, y, now);
  buffs.sort(cmp);
  debuffs.sort(cmp);
  return { buffs, debuffs };
}

export function SelfAuraBar({
  style = DEFAULT_SELF_AURA_BAR_STYLE,
}: {
  readonly style?: SelfAuraBarStyle;
} = {}): ReactElement {
  const { state } = useStore();
  const me = ownEntity(state.hudSnapshot, state.ownSeat);
  const now = state.hudSnapshot?.tick ?? 0;
  const { buffs, debuffs } = selfBarAuras(me?.auras ?? [], now);

  const container: CSSProperties = {
    position: "absolute",
    top: style.topPx,
    right: style.rightPx,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: style.rowGapPx,
    pointerEvents: "none",
  };

  return (
    <div
      data-testid="self-aura-bar"
      data-buff-count={String(buffs.length)}
      data-debuff-count={String(debuffs.length)}
      style={container}
    >
      <AuraRow testId="self-buff-row" auras={buffs} now={now} style={style} />
      <AuraRow testId="self-debuff-row" auras={debuffs} now={now} style={style} />
    </div>
  );
}

function AuraRow({
  testId,
  auras,
  now,
  style,
}: {
  readonly testId: string;
  readonly auras: readonly WireAura[];
  readonly now: number;
  readonly style: SelfAuraBarStyle;
}): ReactElement {
  return (
    <div
      data-testid={testId}
      data-count={String(auras.length)}
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "flex-end",
        gap: style.chipGapPx,
        maxWidth: style.rowMaxWidthPx,
      }}
    >
      {auras.map((aura) => (
        <AuraChip
          key={`${aura.def}:${String(aura.sourceSeat)}:${String(aura.appliedAtTick)}`}
          aura={aura}
          now={now}
          tickRate={TICK_RATE}
          style={style.chip}
          testId="self-aura-chip"
        />
      ))}
    </div>
  );
}
